import { format } from "date-fns";

import { PATIENT_APPOINTMENTS_OPTIONS, PATIENT_CURRENT_APPOINTMENT_OPTIONS } from "components/word-processor-field/merge-tags/merge-tags-options/patient-appointments.utils";
import { MergeTagsReplaceHandler } from "components/word-processor-field/merge-tags/merge-tags-values/merge-tags-values";
import {
  allowTableInsidePTag,
  getFormattedDate,
  getPatientSignatureMergeTagOrPlaceholder
} from "components/word-processor-field/merge-tags/merge-tags.utils";
import { APPOINTMENT_STATUSES } from "constants/appointments";
import { CONTACT_RELATIONSHIP } from "constants/patients";
import { SALE_STATUSES } from "constants/sales";
import { formatMoney, getFullName } from "utils/common";

import { getAdjustmentsTableHtml } from "./common/adjustments-table.utils";
import { getPaymentsTableHtml } from "./common/payments-table.utils";
import { getRefundsTableHtml } from "./common/refunds-table.utils";
import { getSalesItemsTableHtml } from "./common/sale-items-table.utils";

export const PATIENT_APPOINTMENTS_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_DATE]: ({ data }) => {
    if (!data.nextAppointment?.endDate) return null;
    return getFormattedDate(data.nextAppointment?.endDate);
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_DATE_TIME]: ({ data }) => {
    if (!data.nextAppointment?.startDate) return null;
    return format(
      new Date(data.nextAppointment?.startDate),
      "EEEE MMMM dd, yyyy 'at' hh:mm aaa"
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_STAFF_NAME]: ({ data }) => {
    const staffMember = data.nextAppointment?.staffMember;

    if (!staffMember) return null;

    return getFullName({
      firstName: staffMember?.first_name || "",
      lastName: staffMember?.last_name || "",
      title: staffMember?.title,
      suffix: staffMember?.suffix || ""
    });
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_TYPE]: ({ data }) => {
    if (!data.nextAppointment) return null;

    return data.nextAppointment.appointmentType?.name;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_CLINIC]: ({ data }) => {
    if (!data.nextAppointment) return null;

    return `${data.nextAppointment?.clinic.practice?.name} ${data.nextAppointment?.clinic?.name}`;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_STATUS]: ({ data }) => {
    return APPOINTMENT_STATUSES.getFromCode(data.nextAppointment?.status);
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_OUTCOME_NOTES]: ({ data }) => {
    return data.nextAppointment?.outcome_notes;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_ROOM]: ({ data }) => {
    return data.nextAppointment?.room?.name;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_PHYSICIAN_REFERRAL]: ({
    data
  }) => {
    if (!data.nextAppointment?.referringPhysician) return null;

    const referringPhysician = data.nextAppointment?.referringPhysician;

    return getFullName({
      firstName: referringPhysician?.first_name || "",
      lastName: referringPhysician?.last_name || "",
      title: referringPhysician?.title,
      suffix: referringPhysician?.suffix || ""
    });
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_COMPANION_TYPE]: ({
    data
  }) => {
    return CONTACT_RELATIONSHIP.getFromCode(
      data.nextAppointment?.companionType
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_COMPANION_NAME]: ({
    data
  }) => {
    return data.nextAppointment?.companionName;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_NOTES]: ({ data }) => {
    return data.nextAppointment?.notes;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_CHART_NOTES]: ({ data }) => {
    return data.nextAppointment?.chartNote?.text;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_STAFF_SIGNATURE]: ({
    data
  }) => {
    return getPatientSignatureMergeTagOrPlaceholder(
      data.nextAppointment?.staffMember?.signature
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_STAFF_EMAIL]: ({ data }) => {
    return data.nextAppointment?.staffMember?.email ?? "";
  },
  //
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_DATE]: ({ data }) => {
    if (!data.lastAppointment?.startDate) return null;
    return getFormattedDate(data.lastAppointment?.startDate);
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_DATE_TIME]: ({ data }) => {
    if (!data.lastAppointment?.startDate) return null;
    return format(
      new Date(data.lastAppointment?.startDate),
      "EEEE MMMM dd, yyyy 'at' hh:mm aaa"
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_STAFF_NAME]: ({ data }) => {
    const staffMember = data.lastAppointment?.staffMember;

    if (!staffMember) return null;

    return getFullName({
      firstName: staffMember?.first_name || "",
      lastName: staffMember?.last_name || "",
      title: staffMember?.title,
      suffix: staffMember?.suffix || ""
    });
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_TYPE]: ({ data }) => {
    if (!data.lastAppointment) return null;

    return data.lastAppointment.appointmentType?.name;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_CLINIC]: ({ data }) => {
    if (!data.lastAppointment) return null;

    return `${data.lastAppointment?.clinic.practice?.name} ${data.lastAppointment?.clinic?.name}`;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_STATUS]: ({ data }) => {
    return APPOINTMENT_STATUSES.getFromCode(data.lastAppointment?.status);
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_OUTCOME_NOTES]: ({ data }) => {
    return data.lastAppointment?.outcome_notes;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_ROOM]: ({ data }) => {
    return data.lastAppointment?.room?.name;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_PHYSICIAN_REFERRAL]: ({
    data
  }) => {
    if (!data.lastAppointment?.referringPhysician) return null;

    const referringPhysician = data.lastAppointment?.referringPhysician;

    return getFullName({
      firstName: referringPhysician?.first_name || "",
      lastName: referringPhysician?.last_name || "",
      title: referringPhysician?.title,
      suffix: referringPhysician?.suffix || ""
    });
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_COMPANION_TYPE]: ({
    data
  }) => {
    return CONTACT_RELATIONSHIP.getFromCode(
      data.lastAppointment?.companionType
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_COMPANION_NAME]: ({
    data
  }) => {
    return data.lastAppointment?.companionName;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_NOTES]: ({ data }) => {
    return data.lastAppointment?.notes;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_CHART_NOTES]: ({ data }) => {
    return data.lastAppointment?.chartNote?.text;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_STAFF_SIGNATURE]: ({
    data
  }) => {
    return getPatientSignatureMergeTagOrPlaceholder(
      data.lastAppointment?.staffMember?.signature
    );
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_STATUS]: ({ data }) => {
    if (!data.lastAppointmentSale) return null;
    return SALE_STATUSES.getFromCode(data.lastAppointmentSale?.status);
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_TOTAL]: ({ data }) => {
    if (!data.lastAppointmentSale) return null;

    return formatMoney(Number(data.lastAppointmentSale?.total));
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_RESPONSIBILITY]:
    ({ data }) => {
      if (!data.lastAppointmentSale) return null;

      return formatMoney(
        Number(data.lastAppointmentSale?.patient_responsibility_total)
      );
    },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_RESPONSIBILITY_PAID]:
    ({ data }) => {
      if (!data.lastAppointmentSale) return null;

      return formatMoney(
        Number(data.lastAppointmentSale?.patient_responsibility_total_paid)
      );
    },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_LINE_ITEMS_COUNT]:
    ({ data }) => {
      if (!data.lastAppointmentSale) return null;

      return String(data.lastAppointmentSale?.items?.length ?? 0);
    },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_LINE_ITEMS]: ({
    data
  }) => {
    if (!data.lastAppointmentSale?.items?.length) return null;
    return allowTableInsidePTag(
      getSalesItemsTableHtml(data.lastAppointmentSale)
    );
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_PAYMENTS_TOTAL]:
    ({ data }) => {
      if (!data.lastAppointmentSale?.payments?.length) return null;

      return formatMoney(Number(data.lastAppointmentSale.total_paid));
    },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_PAYMENTS]: ({
    data
  }) => {
    if (!data.lastAppointmentSale?.payments?.length) return null;
    return allowTableInsidePTag(getPaymentsTableHtml(data.lastAppointmentSale));
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_ADJUSTMENTS_TOTAL]:
    ({ data }) => {
      return formatMoney(Number(data.lastAppointmentSale?.total_adjusted));
    },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_ADJUSTMENTS]: ({
    data
  }) => {
    if (!data.lastAppointmentSale?.adjustments?.length) return null;
    return allowTableInsidePTag(
      getAdjustmentsTableHtml(data.lastAppointmentSale)
    );
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_REFUNDS_TOTAL]: ({
    data
  }) => {
    return formatMoney(Number(data.lastAppointmentSale?.total_refunds));
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_REFUNDS]: ({
    data
  }) => {
    if (!data.lastAppointmentSale?.refunds?.length) return null;
    return allowTableInsidePTag(getRefundsTableHtml(data.lastAppointmentSale));
  }
};

export const PATIENT_CURRENT_APPOINTMENT_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_DATE]: ({ data }) => {
    if (!data.currentAppointment?.endDate) return null;
    return getFormattedDate(data.currentAppointment?.endDate);
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_DATE_TIME]: ({ data }) => {
    if (!data.currentAppointment?.startDate) return null;
    return format(
      new Date(data.currentAppointment?.startDate),
      "EEEE MMMM dd, yyyy 'at' hh:mm aaa"
    );
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_STAFF_NAME]: ({ data }) => {
    const staffMember = data.currentAppointment?.staffMember;
    if (!staffMember) return null;
    return getFullName({
      firstName: staffMember?.first_name || "",
      lastName: staffMember?.last_name || "",
      title: staffMember?.title,
      suffix: staffMember?.suffix || ""
    });
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_TYPE]: ({ data }) => {
    if (!data.currentAppointment) return null;
    return data.currentAppointment.appointmentType?.name;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_CLINIC]: ({ data }) => {
    if (!data.currentAppointment) return null;
    return `${data.currentAppointment?.clinic.practice?.name} ${data.currentAppointment?.clinic?.name}`;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_STATUS]: ({ data }) => {
    return APPOINTMENT_STATUSES.getFromCode(data.currentAppointment?.status);
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_OUTCOME_NOTES]: ({ data }) => {
    return data.currentAppointment?.outcome_notes;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_ROOM]: ({ data }) => {
    return data.currentAppointment?.room?.name;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_PHYSICIAN_REFERRAL]: ({
    data
  }) => {
    if (!data.currentAppointment?.referringPhysician) return null;
    const referringPhysician = data.currentAppointment?.referringPhysician;
    return getFullName({
      firstName: referringPhysician?.first_name || "",
      lastName: referringPhysician?.last_name || "",
      title: referringPhysician?.title,
      suffix: referringPhysician?.suffix || ""
    });
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_COMPANION_TYPE]: ({
    data
  }) => {
    return CONTACT_RELATIONSHIP.getFromCode(
      data.currentAppointment?.companionType
    );
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_COMPANION_NAME]: ({
    data
  }) => {
    return data.currentAppointment?.companionName;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_NOTES]: ({ data }) => {
    return data.currentAppointment?.notes;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_CHART_NOTES]: ({ data }) => {
    return data.currentAppointment?.chartNote?.text;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_STAFF_SIGNATURE]: ({
    data
  }) => {
    return getPatientSignatureMergeTagOrPlaceholder(
      data.currentAppointment?.staffMember?.signature
    );
  },
};
